import { useEffect } from "react";
import { X } from "lucide-react";

interface MobileNavDrawerProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * Off-canvas navigation drawer for mobile / collapsed-nav widths.
 * Renders the existing <Sidebar> (passed as children) verbatim — the single
 * NAV_ITEMS definition and active-route styling live in Sidebar.tsx, not here.
 * Owns: fixed positioning, slide transform, backdrop, body-scroll-lock,
 * Escape/backdrop-to-close, and a close (X) affordance.
 */
export function MobileNavDrawer({
  open,
  onClose,
  children,
}: MobileNavDrawerProps) {
  // Body-scroll-lock + Escape-to-close while open.
  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop — click to close */}
      <div
        onClick={onClose}
        aria-hidden
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          zIndex: "var(--z-drawer-backdrop)" as unknown as number,
        }}
      />

      {/* Sliding panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "100dvh",
          width: "min(280px, 80vw)",
          zIndex: "var(--z-drawer)" as unknown as number,
          background: "var(--sidebar-bg)",
          borderRight: "1px solid var(--border-subtle)",
          transform: "translateX(0)",
          transition: "transform 180ms ease",
          display: "grid",
          gridTemplateRows: "1fr",
          overflow: "hidden",
        }}
      >
        {/* Close (X) affordance — floats over the Sidebar brand block */}
        <button
          onClick={onClose}
          aria-label="Close navigation"
          title="Close navigation"
          style={{
            position: "absolute",
            top: 14,
            right: 12,
            zIndex: 1,
            background: "transparent",
            border: "none",
            height: "var(--tap-min)",
            width: "var(--tap-min)",
            display: "grid",
            placeItems: "center",
            color: "var(--text-secondary)",
            cursor: "pointer",
            borderRadius: "var(--radius-input)",
          }}
        >
          <X size={18} strokeWidth={1.6} />
        </button>
        {children}
      </div>
    </>
  );
}
